import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { Slot } from "radix-ui";

import { cn } from "../../lib/utils";

/* The Sirdab badge: a status MARKER, never an action. Trued after the
   shadcn add: the caption type role replaces the stock x-small utility,
   the focus ring classes go (the global focus-visible outline owns
   focus), bg-accent hovers go (shadcn-compat token, never ours), and
   destructive is the subtle banner pair, never a red fill: status
   reports, it never commands. The bridge carries the density
   (control-xs height, padding).

   Variants (pick by what the badge REPORTS):
     default      the ink pill: a count or a "new" marker.
     secondary    a neutral state (draft, snapshot, n/a): the quiet fill.
     destructive  a failed or breached state: destructive-subtle with
                  destructive text, the one status pair whose text
                  carries the hue (errors are red text).
     success      a healthy state (paid, live, on target): the
                  success-subtle banner pair with foreground text.
     warning      a state to watch (low stock, margin alert): the
                  warning-subtle banner pair with foreground text.
     outline      a hairline pill for labels that are not a status.
     ghost        a bare caption in the quiet tone.
     link         an inline count that navigates (asChild around a Link).

   Success and warning keep FOREGROUND text: TOKENS.md pairs the
   *-subtle banners with --foreground, and green or amber text on its
   own tint reads as a button. The lookbook's badges section shows the
   whole set. */
const badgeVariants = cva(
  "inline-flex w-fit shrink-0 items-center justify-center gap-1 overflow-hidden rounded-full border border-transparent px-2 py-0.5 text-caption whitespace-nowrap transition-colors duration-75 [&>svg]:pointer-events-none [&>svg]:size-3",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground",
        secondary: "bg-muted text-foreground",
        destructive: "bg-destructive-subtle text-destructive",
        success: "bg-success-subtle text-foreground",
        warning: "bg-warning-subtle text-foreground",
        outline: "border-border text-foreground",
        ghost: "text-muted-foreground",
        link: "text-primary underline-offset-4",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

function Badge({
  className,
  variant = "default",
  asChild = false,
  ...props
}: React.ComponentProps<"span"> &
  VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot.Root : "span";

  return (
    <Comp
      data-slot="badge"
      data-variant={variant}
      className={cn(badgeVariants({ variant }), className)}
      {...props}
    />
  );
}

export { Badge, badgeVariants };
